import {
  Button,
  Modal,
  TextAreaInput,
  TextInput,
  Select,
  SelectOptionType,
  addToast,
} from '@octano/global-ui';
import {
  Ref,
  forwardRef,
  useImperativeHandle,
  useMemo,
  useState,
  useCallback,
} from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';
import { useValidations } from '../../../hooks/useValidations';
import { TranslationsKeys } from '../../../locales/translations';
import { createSectionClassActivity, updateSectionClassActivity } from '../api';
import {
  CourseLearningUnit,
  IClassesBookClassItem,
  LessonActivity,
} from '../types';
import ModalAddActivityDetails from './ModalAddActivityDetails';
export type ModalAddActivityMethods = {
  open: (
    classItem: IClassesBookClassItem,
    activityItem?: LessonActivity,
  ) => void;
  close: () => void;
};

type ModalAddActivityProps = {
  onSave?: () => void;
  units: CourseLearningUnit[];
};

type FormFields = {
  courseLearningUnitId: SelectOptionType | null;
  criteria: string | null;
  contents: string | null;
  activities: string | null;
};

const TEXT_SIZE = 500;

const ModalAddActivity = (
  { units, onSave }: ModalAddActivityProps,
  ref: Ref<ModalAddActivityMethods>,
) => {
  const { t } = useTranslation(TranslationsKeys.CLASSES_BOOK_EDIT);

  const [keyPrefix, setKeyPrefix] = useState<string>('modalAdd');
  const [visible, setVisible] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [classItem, setClassItem] = useState<IClassesBookClassItem>();
  const [activityItem, setActivityItem] = useState<LessonActivity>();

  const { validateMaxLength } = useValidations();

  const { control, reset, watch, handleSubmit } = useForm<FormFields>({
    defaultValues: {
      contents: '',
      criteria: '',
      activities: '',
      courseLearningUnitId: null,
    },
  });

  const [courseLearningUnitV, contentsV, activitiesV, criteriaV] = watch([
    'courseLearningUnitId',
    'contents',
    'activities',
    'criteria',
  ]);

  const selectedUnit = useMemo(
    () =>
      courseLearningUnitV?.value
        ? units.find((u) => u.id === courseLearningUnitV?.value) || null
        : null,
    [units, courseLearningUnitV],
  );

  const close = useCallback(() => {
    setVisible(false);
    setClassItem(undefined);
    setActivityItem(undefined);
    setKeyPrefix('modalAdd');
    reset({
      criteria: '',
      contents: '',
      activities: '',
      courseLearningUnitId: null,
    });
  }, [reset]);

  const open = useCallback(
    (
      propClassItem: IClassesBookClassItem,
      propActivityItem?: LessonActivity,
    ) => {
      setClassItem(propClassItem);
      setActivityItem(propActivityItem);

      if (propActivityItem) {
        setKeyPrefix('modalEdit');
        const findedUnit = units?.find(
          (u) => u.id === propActivityItem.courseLearningUnitId,
        );
        reset({
          criteria: propActivityItem.criteria || '',
          contents: propActivityItem.contents || '',
          activities: propActivityItem.activities || '',
          courseLearningUnitId: findedUnit?.id
            ? { label: findedUnit?.title, value: findedUnit?.id }
            : null,
        });
      } else {
        setKeyPrefix('modalAdd');
        reset({
          criteria: '',
          contents: '',
          activities: '',
          courseLearningUnitId: null,
        });
      }

      setVisible(true);
    },
    [reset, units],
  );

  useImperativeHandle(ref, () => ({ open, close }));

  const handleCreate = useCallback(
    async (values: FormFields) => {
      if (!classItem?.id || !values?.courseLearningUnitId?.value) {
        return { error: 'NOT_VALID_PARAMS' };
      }
      const payload = {
        ...values,
        courseLearningUnitId: +values.courseLearningUnitId.value,
      };
      const { error } = await createSectionClassActivity(
        classItem?.sectionId,
        classItem?.id,
        payload,
      );

      if (!error) {
        return { error: null };
      }
      return { error: error?.data?.message };
    },
    [classItem],
  );

  const handelUpdate = useCallback(
    async (values: FormFields) => {
      if (
        !classItem?.id ||
        !activityItem?.id ||
        !values?.courseLearningUnitId?.value
      ) {
        return { error: 'NOT_VALID_PARAMS' };
      }
      const payload = {
        ...values,
        courseLearningUnitId: +values.courseLearningUnitId.value,
      };
      const { error } = await updateSectionClassActivity(
        classItem?.sectionId,
        activityItem?.id,
        payload,
      );

      if (!error) {
        return { error: null };
      }
      return { error: error?.data?.message };
    },
    [classItem, activityItem],
  );

  const handleProcess = useCallback(
    async (values: FormFields) => {
      if (loading || !classItem?.id || !values?.courseLearningUnitId?.value) {
        return;
      }
      setLoading(true);
      const { error } = await (activityItem?.id
        ? handelUpdate(values)
        : handleCreate(values));
      setLoading(false);
      if (!error) {
        addToast({
          text: t(`${keyPrefix}.success`),
          color: 'success',
          icon: 'success',
        });
        close();
        !!onSave && onSave();
      } else {
        if (['TEACHER_HASNT_ACCESS', 'MAX_LIMIT_REACHED'].includes(error)) {
          addToast({
            text: t(`${keyPrefix}.errors.${error}`),
            color: 'danger',
            icon: 'error',
          });
        } else {
          addToast({
            text: t(`${keyPrefix}.failed`),
            color: 'danger',
            icon: 'error',
          });
        }
      }
    },
    [
      activityItem,
      classItem,
      loading,
      onSave,
      close,
      handelUpdate,
      handleCreate,
      t,
      keyPrefix,
    ],
  );

  return (
    <Modal isOpen={visible} toggle={close} size="xl" unmountOnClose>
      <Row>
        <Col xs={12} md={12} lg={4} />
        <Col xs={12} md={6} lg={4}>
          <p className="fs-22 text-dark text-center mb-4">
            {t(`${keyPrefix}.title`)}
          </p>
        </Col>
        <Col xs={12} md={6} lg={4}>
          <ModalAddActivityDetails
            prefix={keyPrefix}
            classDate={classItem?.date}
            className={classItem?.name}
          />
        </Col>
      </Row>

      <Row className="mt-4">
        <Col xs={12} md={9}>
          <Select
            control={control}
            name="courseLearningUnitId"
            label={t(`${keyPrefix}.form.unit`)}
            placeholder={t(`${keyPrefix}.form.unit`)}
            options={units.map((e) => ({ label: e.title, value: e.id }))}
          />
        </Col>
        <Col xs={12} md={3}>
          <TextInput
            name="hours"
            value={selectedUnit?.hours || ''}
            label={t(`${keyPrefix}.form.hours`)}
            placeholder={t(`${keyPrefix}.form.hours`)}
            disabled
          />
        </Col>
        <Col xs={12} md={6} lg={4}>
          <TextAreaInput
            control={control}
            name="criteria"
            label={t(`${keyPrefix}.form.criteria`)}
            placeholder={t(`${keyPrefix}.form.criteria`)}
            maxLength={TEXT_SIZE}
            rules={{
              validate: {
                maxLength: validateMaxLength(TEXT_SIZE),
              },
            }}
          />
          <span>{`${criteriaV?.length || 0}/${TEXT_SIZE}`}</span>
        </Col>
        <Col xs={12} md={6} lg={4}>
          <TextAreaInput
            control={control}
            name="contents"
            label={t(`${keyPrefix}.form.contents`)}
            placeholder={t(`${keyPrefix}.form.contents`)}
            maxLength={TEXT_SIZE}
            rules={{
              validate: {
                maxLength: validateMaxLength(TEXT_SIZE),
              },
            }}
          />
          <span>{`${contentsV?.length || 0}/${TEXT_SIZE}`}</span>
        </Col>
        <Col xs={12} md={12} lg={4}>
          <TextAreaInput
            control={control}
            name="activities"
            label={t(`${keyPrefix}.form.activities`)}
            placeholder={t(`${keyPrefix}.form.activities`)}
            maxLength={TEXT_SIZE}
            rules={{
              validate: {
                maxLength: validateMaxLength(TEXT_SIZE),
              },
            }}
          />
          <span>{`${activitiesV?.length || 0}/${TEXT_SIZE}`}</span>
        </Col>
      </Row>
      <Row className="pt-5">
        <Col xs={12} lg={6} className="pb-2 order-2 order-lg-1">
          <Button
            type="button"
            outlined
            onClick={close}
            disabled={loading}
            text={t(`${keyPrefix}.close`)}
            fullwidth
          />
        </Col>
        <Col xs={12} lg={6} className="pb-2 order-1 order-lg-2">
          <Button
            text={t(`${keyPrefix}.save`)}
            fullwidth
            disabled={!selectedUnit?.id}
            loading={loading}
            onClick={handleSubmit(handleProcess)}
          />
        </Col>
      </Row>
    </Modal>
  );
};

export default forwardRef(ModalAddActivity);
